import {
  Box,
  IconButton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TooltipProps,
  tooltipClasses,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditLineIcon, Setting01Icon } from '~/assets/icons';
import { cast } from '~/utils/helper';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { PAGE_INDEX, PAGE_SIZE } from '~/utils/constants/common';
import { deviceActions } from '~/thunks/device/deviceSlice';
import BasePagination from '~/components/jobs/base-pagination/BasePagination';
import DeviceConfigurationDialog from './DeviceConfigurationDialog';
import UpdateDeviceDialog from './UpdateDeviceDialog';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#404854',
    boxShadow: '0px 4px 12px #509EE833',
    border: 1,
    fontSize: theme.typography.pxToRem(15),
  },
}));

const getDeviceStatusLabel = (status?: number | string, t?: any) => {
  const normalizedStatus = Number(status);

  if (normalizedStatus === 2) {
    return t ? t('status.broken') : 'Hỏng';
  }
   if (normalizedStatus === 0) {
    return t ? t('status.unassigned') : 'Chưa gán';
  }
 if (normalizedStatus === 1) {
    return t ? t('status.assigned') : 'Đã gán';
  }


  return t ? t('status.normal') : 'Bình thường';
};

const DeviceList = () => {
  const { t } = useTranslation('admin/device');
  const dispatch = useAppDispatch();
  const { devices, totalItems, filter, isLoading } = useAppSelector((state) => state.device);
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const currentUser = Object.values(selectedUser).length === 0 ? userProfile : selectedUser;
  const [configurationImei, setConfigurationImei] = useState<string | null>(null);
  const [editingDevice, setEditingDevice] = useState<any | null>(null);

  const handleSort = (column: string) => {
    if (filter.sortBy === column) {
      dispatch(deviceActions.setFilter({ sortOrder: filter.sortOrder === 'asc' ? 'desc' : 'asc', pageIndex: PAGE_INDEX }));
      return;
    }
    dispatch(deviceActions.setFilter({ sortBy: column, sortOrder: 'asc', pageIndex: PAGE_INDEX }));
  };

  const tableHeaders = [
    ['device_imei', t('table.imei')],
    ['province_name', t('table.province')],
    ['ward_name', t('table.ward')],
    ['status', t('table.status')],
    ['note', t('table.note')],
    ['created_at', t('table.createdAt')],
  ];

  const handleChangePage = (newPage: number) => {
    dispatch(deviceActions.setFilter({ pageIndex: newPage }));
  };

  const handleChangePageSize = (pageSize: number | string) => {
    dispatch(
      deviceActions.setFilter({
        pageSize: Number(pageSize),
        pageIndex: PAGE_INDEX,
      })
    );
  };

  return (
    <Box className='bg-white rounded-xl p-4'>
      <TableContainer className='rounded-t-xl border-b-0 bg-white overflow-x-auto'>
        <Table stickyHeader className='min-w-max'>
          <TableHead>
            <TableRow>
              <TableCell
                align='center'
                className='w-16 border-b-0 sticky left-0 z-10 bg-[#DCE8F5] text-[#161A1F] text-sm font-bold'
              >
                {t('STT', { ns: 'admin/common' })}
              </TableCell>
              <TableCell
                align='center'
                className='w-24 border-b-0 sticky left-14 z-10 bg-[#DCE8F5] text-[#161A1F] text-sm font-bold'
              >
                {t('action', { ns: 'admin/common' })}
              </TableCell>
              {tableHeaders.map(([key, label]) => (
                <TableCell
                  key={key}
                  align='center'
                  className='w-40 border-b-0 bg-[#DCE8F5] text-[#161A1F] text-sm font-bold cursor-pointer'
                  onClick={() => handleSort(key)}
                >
                  {label}
                  {filter.sortBy === key ? (filter.sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.length > 0 ? (
              devices.map((row: any, index) => (
                <TableRow
                  key={`${row.id ?? row.device_id ?? index}`}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#F5F8FC',
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: '1px solid #EBF1F8',
                      borderLeft: 'none',
                      borderRight: 'none',
                    },
                  }}
                >
                  <TableCell
                    align='center'
                    className='text-base font-normal text-[#161A1F] border-b-0'
                    sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'white' }}
                  >
                    {index + 1 + (Number(filter.pageIndex || 1) - 1) * Number(filter.pageSize || 10)}
                  </TableCell>
                  <TableCell
                    align='center'
                    className='text-base font-normal text-[#161A1F] border-b-0'
                    sx={{ position: 'sticky', left: 56, zIndex: 1, backgroundColor: 'white' }}
                  >
                    <Box className='flex justify-center'>
                      <LightTooltip title={t('tooltip.edit', { ns: 'admin/common' })}>
                        <IconButton
                          className='hover:bg-[#EAF1F9] p-2'
                          onClick={() => setEditingDevice(row)}
                        >
                          <EditLineIcon />
                        </IconButton>
                      </LightTooltip>
                      <LightTooltip title={t('configurationDialog.tooltip')}>
                        <IconButton
                          className='hover:bg-[#EAF1F9] p-2'
                          onClick={() => setConfigurationImei(cast(row.imei))}
                        >
                          <Setting01Icon />
                        </IconButton>
                      </LightTooltip>
                    </Box>
                  </TableCell>
                  <TableCell align='center' className='text-base font-normal text-[#161A1F] border-b-0' sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {cast(row.imei)}
                  </TableCell>
                  <TableCell align='center' className='text-base font-normal text-[#161A1F] border-b-0' sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {cast(row.province_name)}
                  </TableCell>
                  <TableCell align='center' className='text-base font-normal text-[#161A1F] border-b-0' sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {cast(row.ward_name)}
                  </TableCell>
                  <TableCell align='center' className='text-base font-normal text-[#161A1F] border-b-0' sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {getDeviceStatusLabel(row.status, t)}
                  </TableCell>
                  <TableCell align='center' className='text-base font-normal text-[#161A1F] border-b-0' sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {cast(row.note)}
                  </TableCell>
                  <TableCell align='center' className='text-base font-normal text-[#161A1F] border-b-0' sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY HH:mm') : ''}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align='center'>
                  {isLoading ? 'Đang tải...' : t('noData', { ns: 'admin/common' })}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <BasePagination
        label={t('list')}
        pageIndex={filter.pageIndex || PAGE_INDEX}
        pageSize={filter.pageSize || PAGE_SIZE}
        totalItems={totalItems}
        count={Math.max(PAGE_INDEX, Math.ceil(Number(totalItems || 0) / (filter.pageSize || PAGE_SIZE)))}
        onChange={(_, value: number) => handleChangePage(value)}
        onChangePageSize={handleChangePageSize}
      />
      <DeviceConfigurationDialog
        open={Boolean(configurationImei)}
        imei={configurationImei || ''}
        onClose={() => setConfigurationImei(null)}
      />
      <UpdateDeviceDialog
        open={Boolean(editingDevice)}
        device={editingDevice}
        currentUser={currentUser}
        onClose={() => setEditingDevice(null)}
        onUpdated={() => dispatch(deviceActions.setNeedRefreshData(true))}
      />
    </Box>
  );
};

export default memo(DeviceList);